import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Badge } from "../../../components/ui/Badge";
import { Card } from "../../../components/ui/Card";
import { type Column, Table } from "../../../components/ui/Table";
import { EmptyState, ErrorState, LoadingState } from "../../../components/ui/State";
import { ApiError } from "../../../lib/api";
import type {
  NetworkViolationRow,
  NetworkViolationsFilters,
} from "../../../lib/api/network/violations";
import { networkViolationsApi } from "../../../lib/api/network/violations";
import { formatDateTime } from "../../../lib/format";
import { formatMessage, t } from "../../../lib/i18n";
import { useInsightsScope } from "../../../lib/useInsightsScope";

const RAW_LIMIT = 25;

export type PivotDimension = "destination" | "rule";

interface Props {
  dimension: PivotDimension;
  /** Filter value: destination string or matched_rule_id UUID. */
  value: string;
  /** Display label; defaults to ``value``. Useful for rules where the
   *  UUID is the technical key but the user clicked a human name. */
  label?: string;
  onClose: () => void;
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function buildFilter(dim: PivotDimension, val: string): NetworkViolationsFilters {
  if (dim === "destination") return { search: val };
  return { matched_rule_id: val };
}

function viewAllHref(dim: PivotDimension, val: string): string {
  const params = new URLSearchParams(window.location.search);
  params.set("pivot", "raw-events");
  params.delete("detail");
  params.delete("label");
  if (dim === "destination") params.set("search", val);
  else params.set("matched_rule_id", val);
  return `/insights/violations?${params.toString()}`;
}

/**
 * Reusable side panel opened from the destination and rule pivot tabs. Pulls
 * the latest in-scope raw events for the clicked row, shows
 * summary badges, and offers a one-click jump to the full Raw events tab
 * pre-filtered to the same value.
 *
 * URL contract: ``?detail=<value>`` (and optionally ``?label=<encoded>`` for
 * rules, where the UUID is the canonical key but the user-friendly name is
 * the rule's display name). Devices have their own panel via /devices?id=
 * so this component never handles ``dimension="device"``.
 */
export function PivotDrillDownPanel({ dimension, value, label, onClose }: Props) {
  const scope = useInsightsScope();
  const filter = buildFilter(dimension, value);

  const query = useQuery({
    queryKey: ["violations", "drilldown", dimension, value, scope],
    queryFn: () =>
      networkViolationsApi.list({
        ...scope,
        ...filter,
        limit: RAW_LIMIT,
      }),
  });

  const items: NetworkViolationRow[] = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  // This panel samples raw per-attempt rows, which are purged after
  // `raw_retention_days`. When the scope reaches before that horizon the
  // aggregated bucket counts come from the rollup but these samples only
  // cover the retained window — note that so the two don't look inconsistent.
  const retentionDays = query.data?.raw_retention_days;
  const beyondRetention = (() => {
    if (!retentionDays || !scope.from) return false;
    const horizon = new Date();
    horizon.setUTCDate(horizon.getUTCDate() - retentionDays);
    return new Date(scope.from) < horizon;
  })();
  const distinctDevices = new Set(items.map((r) => r.device_id)).size;
  const distinctDestinations = new Set(items.map((r) => r.destination)).size;

  const titleText = label ?? value;
  const eyebrow = t(`insights.violations.detail.eyebrow.${dimension}`);

  const baseColumns: Column<NetworkViolationRow>[] = [
    {
      key: "time",
      header: t("insights.violations.column.lastSeen"),
      render: (r) => (
        <span className="mono table-cell--datetime">{formatDateTime(r.attempted_at)}</span>
      ),
    },
    {
      key: "device",
      header: t("insights.violations.column.device"),
      render: (r) => (
        <Link to={`/devices?id=${encodeURIComponent(r.device_id)}`}>
          <span className="mono">{r.device_uid || r.device_id}</span>
        </Link>
      ),
    },
  ];

  const columns: Column<NetworkViolationRow>[] =
    dimension === "rule"
      ? [
          ...baseColumns,
          {
            key: "destination",
            header: t("insights.violations.column.destination"),
            render: (r) => <span className="mono">{r.destination}</span>,
          },
        ]
      : baseColumns;

  return (
    <>
      <div className="master-detail__panel-header">
        <div style={{ minWidth: 0 }}>
          <div className="muted" style={{ fontSize: 12 }}>{eyebrow}</div>
          <h2 style={{ margin: "4px 0 6px", wordBreak: "break-all" }}>{titleText}</h2>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Badge tone="warning">
              {formatMessage("insights.violations.detail.attempts", {
                count: total.toLocaleString(),
              })}
            </Badge>
            <Badge tone="info">
              {formatMessage("insights.violations.detail.devices", {
                count: String(distinctDevices),
              })}
            </Badge>
            {dimension === "rule" && distinctDestinations > 0 && (
              <Badge tone="neutral">
                {formatMessage("insights.violations.detail.destinations", {
                  count: String(distinctDestinations),
                })}
              </Badge>
            )}
          </div>
        </div>
        <button
          type="button"
          className="master-detail__panel-close"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          &times;
        </button>
      </div>

      <div className="master-detail__panel-body">
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          <Link to={viewAllHref(dimension, value)} className="btn btn--sm btn--secondary">
            {t("insights.violations.detail.viewAllRaw")}
          </Link>
        </div>

        {beyondRetention && retentionDays && (
          <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
            {formatMessage("insights.violations.detail.retentionNote", {
              days: String(retentionDays),
            })}
          </p>
        )}

        <Card title={t("insights.violations.detail.recentTitle")} padding="none">
          {query.isLoading && <LoadingState />}
          {query.error != null && (
            <ErrorState
              titleKey="states.errorTitle"
              message={translateError(query.error)}
            />
          )}
          {query.data && items.length === 0 && (
            <EmptyState
              titleKey="insights.violations.detail.empty.title"
              bodyKey="insights.violations.detail.empty.body"
            />
          )}
          {items.length > 0 && (
            <Table rows={items} columns={columns} rowKey={(r) => r.id} empty={null} />
          )}
        </Card>
      </div>
    </>
  );
}
